import { LocaleData } from '../../../models/locale/locale-data';
import { LandingVariantCopy } from '../../../models/landing/variant';

// New-UX (`-v1`) shared copy — the approved redesign strings (mockup pack
// round 3, 26-Aug-2026 product feedback). The three `-v1` variant keys reuse
// this block; ` **` footnote markers on the CTA headings are appended by the
// components, not stored here.
const v2Copy: LandingVariantCopy = {
  banner: 'REGISTREER NU',
  heroCopy: 'Wat een slimme aankoop!',
  heroSubCopy1: 'Registreer uw apparaat binnen 60 dagen om uw gratis voordelen te ontgrendelen',
  benefitTicks: [
    '10 jaar gratis garantie op de onderdelen*',
    'Originele Beko-onderdelen',
    'Optionele extra bescherming tegen onopzettelijke schade en storingen',
  ],
  heroDisclaimer:
    'De garantie dekt onderdelen zoals motoren, pompen, besturingsprintplaten en andere in aanmerking komende componenten.',
  registration: 'Bel nu om te activeren',
  ctaPhoneText: 'Bel 028 989 961',
  ctaPhoneLink: 'tel:+3228989961',
  phoneSubCopy: 'Bereikbaar van maandag tot en met vrijdag van 8:00 tot 18:00 uur.',
  onlineCopy: 'Registreer online in enkele minuten',
  ctaOnlineText: 'Online registreren',
  sixtyDayHeader: 'Registreer binnen 60 dagen',
  sixtyDayBody:
    'Activeer uw gratis 10-jarige garantie op onderdelen voordat uw registratieperiode afloopt',
};

// LP1 hybrid (`phone-only-v2`, Sept 2026) — LP1 COPY V2_SEPT 2026 2.xlsx (BEKO tab)
// as applied in the approved mockups (claude-notes/lp-hybrid-lp1). Every line
// matches Beko control or V1. Dials the same number as V1: `ctaPhoneLink` is
// V1's (undefined = the base link). The `**` on the first heading is appended
// by PhoneOnlyV2.
const lp1HybridCopy: LandingVariantCopy = {
  banner: 'Registreer nu',
  offerHeadline: ['10 jaar garantie', 'op onderdelen*'],
  sixtyDayHeader: 'Registreer uw apparaat binnen 60 dagen na aankoop',
  registration: 'Bel nu om uw apparaat te registreren',
  ctaPhoneText: 'Bel 028 989 961',
  ctaPhoneLink: v2Copy.ctaPhoneLink,
  benefitsHeader: 'Ontgrendel nu uw gratis voordelen:',
  benefitTicks: [
    '10 jaar gratis garantie op onderdelen',
    'Originele onderdelen van Beko',
    'Optionele aanvullende bescherming tegen accidentele schade en defecten',
  ],
  phoneSubCopy: 'Wij zijn bereikbaar van maandag tot en met vrijdag van 08.00 tot 18.00 uur.',
  repeatHeading: 'Klaar om uw apparaat te registreren?',
  ctaPhoneRepeatText: 'Bel nu: 028 989 961',
};

export const data: LocaleData = {
  banner: 'Activeer uw GRATIS',
  heroNumber: '10',
  heroCopy: 'jaar garantie op de onderdelen*',
  heroSubCopy1: '<b>Activering nodig</b>',
  heroSubCopy2: 'binnen 60 dagen na aankoop van uw apparaat',
  registration: 'Bel nu en activeer nu**',
  ctaPhoneText: '028 989 961',
  ctaPhoneLink: 'tel:028989961',
  phoneSubCopy:
    'Activering via telefoon is snel en geeft u extra informatie over uw fabrieksgarantie en 10 jaar garantie op onderdelen. De directe activeringslijn is open van maandag t.e.m. vrijdag van 08:00-18:00.',
  onlineCopy: '',
  ctaOnlineText: '',
  ctaOnlineLink: 'https://beko.register-appliance.com/RegistroPorMarca?country=BE&language=nl',
  onlineJourneyLaunch: 'Live',
  variants: {
    'phone-only-v1': v2Copy,
    'phone-and-link-v1': v2Copy,
    'phone-only-v2': lp1HybridCopy,
    'link-only-v1': {
      ...v2Copy,
      ctaOnlineText: 'Online registreren',
      linesClosedMessage:
        'Onze telefoonlijnen zijn momenteel gesloten, maar u kunt zich op elk moment online registreren',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG registreert uw huishoudapparaat. Domestic & General Insurance Europe AG zal u verzekeringen aanbieden voor uw huishoudapparaten, mocht u geïnteresseerd zijn. Voor informatie over hoe Beko Belgium NV en Domestic & General Insurance Europe AG uw data gebruiken zie: <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
    // LP3 COPY V1_JULY 2026.xlsx (BEKO tab; ** added per master sheet).
    'link-only': {
      registration: 'Registreer uw apparaat nu om uw gratis voordelen te ontgrendelen**',
      onlineCopy:
        'Onze telefoonlijnen zijn momenteel gesloten, maar u kunt zich op elk moment online registreren',
      ctaOnlineText: 'Online registreren',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG registreert uw huishoudapparaat. Domestic & General Insurance Europe AG zal u verzekeringen aanbieden voor uw huishoudapparaten, mocht u geïnteresseerd zijn. Voor informatie over hoe Beko Belgium NV en Domestic & General Insurance Europe AG uw data gebruiken zie: <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
  },
  benefitsHeader: 'Voordelen van activering:',
  benefits: [
    'U krijgt 10 jaar gratis onderdelengarantie',
    'Uw aankoopbewijs in digitaal formaat',
    'Belangrijke updates over apparaten en veiligheid',
  ],
  footerLinks: [
    { text: 'Juridische Informatie', link: '/terms' },
    { text: 'Privacyverklaring', link: 'https://www.domesticandgeneral.com/beko' },
    { text: 'Cookiebeleid', link: '/cookies' },
  ],
  footerCopy:
    'Deze website is eigendom van Domestic & General Group Limited en beheerd door Domestic & General Insurance Europe AG, Hagenauer Straße 44, 65203 Wiesbaden, Duitsland.  Domestic & General Insurance Europe AG is geautoriseerd en gereguleerd door de Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin) in Duitsland.',
  heroAsterix: [
    '* Registreer uw apparaat binnen 60 dagen na aankoop en ontvang 10 jaar garantie op alle onderdelen van uw apparaat (d.w.z. een duur van 10 jaar inclusief de fabrieksgarantie). In geval van een defect, ook na afloop van de fabrieksgarantie, vervangen wij het onderdeel gratis (verplaatsings- en arbeidskosten blijven voor uw rekening). Om van de 10 jaar gratis garantie op onderdelen gebruik te kunnen maken, dienen reparaties te worden uitgevoerd door erkende monteurs van Beko. Algemene voorwaarden zijn van toepassing op deze promotie. Dit aanbod is alleen geldig in België. Voordat u van deze promotie gebruik maakt, dient u de bij het apparaat geleverde documentatie te lezen of de website <0>https://www.beko.com/be-nl</0> te bezoeken.',
  ],
  heroAsterixLinks: ['https://www.beko.com/be-nl'],
  phoneNumberAsterix: [
    '** Vast nummer, beheerd door Domestic & General Insurance Europe AG. Het gebruikelijke lokale beltarief van uw provider is van toepassing. Domestic & General Insurance Europe AG zal u verzekeringen aanbieden voor uw huishoudapparaten mocht u geïnteresseerd zijn. Voor informatie over hoe Beko Belgium NV en Domestic & General Insurance Europe AG uw data gebruiken zie: <0>https://www.domesticandgeneral.com/beko</0>',
  ],
  phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
  additionalSmallPrint: '',
  languageSwitcherLabel: 'Land',
  languageSwitcherPlaceholder: 'Selecteer uw land',
  languageList: [
    { label: 'Duitsland', value: 'de-DE' },
    { label: 'Frankrijk', value: 'fr-FR' },
    { label: 'Italië', value: 'it-IT' },
    { label: 'Spanje', value: 'es-ES' },
    { label: 'Nederland', value: 'nl-NL' },
    { label: 'België (Frans)', value: 'be-FR' },
    { label: 'België (Nederlands)', value: 'be-NL' },
  ],
  cookiePolicy: {
    title: 'Cookiebeleid',
    sections: [
      {
        heading: 'Wat zijn cookies?',
        description: [
          'Cookies zijn kleine tekstbestanden die naar uw apparaat worden verzonden wanneer u een website bezoekt. Ze blijven op uw apparaat staan en worden bij elk volgend bezoek teruggestuurd naar de website waar ze vandaan kwamen. Cookies verzamelen gegevens over u, zoals uw taalvoorkeur of inloggegevens. Deze cookies worden door ons geplaatst en heten first-party cookies. We gebruiken ook third-party cookies, dit zijn cookies van een ander domein dan dat van de website die u bezoekt. Deze cookies kunnen ons helpen onze advertenties en marketing te verbeteren. We gebruiken cookies en andere trackingtechnologieën voor de volgende doeleinden:',
        ],
      },
      {
        heading: 'Cookies beheren',
        description: [
          'Om uw cookie-instellingen te wijzigen, kunt u onze cookiebeheertool gebruiken of dit doen via het beveiligingspictogram dat op elke pagina te vinden is. U kunt het gebruik van sommige of alle cookies en vergelijkbare technologieën die we op de website gebruiken, blokkeren door op de verschillende cookiecategorieën te klikken. Houd er echter rekening mee dat dit invloed kan hebben op uw ervaring met de website en de diensten die we kunnen aanbieden. Houd er ook rekening mee dat als u alle cookies uit uw browser verwijdert, ook alle cookies worden verwijderd die uw voorkeuren opslaan.',
          'De meeste browsers geven aan hoe u het accepteren van nieuwe cookies kunt stoppen, hoe u ervoor kunt zorgen dat de browser u waarschuwt wanneer u een nieuwe cookie ontvangt en hoe u cookies volledig kunt uitschakelen. Om bestaande cookies die eerder in uw browser zijn geplaatst te verwijderen, kunt u de optie selecteren om uw browsegeschiedenis te wissen en cookies te verwijderen of te wissen.',
          "Sommige browsers, zoals Chrome en Firefox, bieden u de mogelijkheid om uw instellingen te wijzigen om in de 'incognito'-modus te browsen. Hiermee wordt de hoeveelheid gegevens die op uw apparaat wordt geplaatst, beperkt en worden alle permanente cookies die op uw apparaat zijn geplaatst automatisch verwijderd wanneer u uw browsersessie beëindigt.",
          'U kunt ook een bezoek brengen aan www.aboutcookies.org. Hier vindt u uitgebreide informatie over hoe u cookies in verschillende browsers kunt verwijderen.',
          "Wilt u meer weten over cookies in het algemeen en hoe u kunt controleren welke derden momenteel cookies op uw mobiele apparaat hebben ingeschakeld en hoe u zich voor sommige van die cookies kunt afmelden, ga dan naar www.networkadvertising.org/mobile-choices . Op uw mobiele apparaat kunt u ook 'Beperk advertentietracking' (op iOS-apparaten) of 'Op interesse gebaseerde advertenties uitzetten' (op Android) selecteren, waarmee u het gebruik van informatie over uw app-gebruik kunt beperken om advertenties te tonen die zijn afgestemd op uw interesses.",
        ],
      },
      {
        heading: 'Websites van derden',
        description: [
          'De website kan links bevatten of naar andere websites linken. Wanneer u op de gelinkte website klikt, verlaat u de website en de inhoud die onder ons beheer vallen en wordt u doorgelinkt naar de website van derden. Wij hebben geen controle over de inhoud van de gelinkte website en kunnen niet verantwoordelijk worden gehouden voor de informatie op en de werking van de gelinkte website. We zullen echter de link naar de gelinkte website verwijderen zodra we kennis krijgen van illegale of anderszins inhoud op de gelinkte website. De beheerders van de gelinkte website kunnen informatie van u verzamelen die door hen zal worden gebruikt in overeenstemming met hun privacybeleid, dat kan afwijken van ons privacybeleid. Wij hebben geen controle over de instellingen van de cookies op de gelinkte website, dus we raden u aan de gelinkte websites te raadplegen voor meer informatie over hun cookies en hoe u deze kunt beheren.',
        ],
      },
      {
        heading: 'Beveiliging',
        description: [
          'We hechten veel waarde aan de veiligheid van de website en een fraudebestendige klantinterface. Alle uitgewisseld informatie wordt versleuteld met SSL-software en we hebben er alles aan gedaan om uw persoonlijke gegevens te beschermen tegen ongeautoriseerde toegang.',
        ],
      },
    ],
  },
  termsAndConditions: {
    title: 'Juridische Informatie',
    content: [
      {
        heading: 'Algemene voorwaarden van de website',
      },
      {
        heading: '1. Definities',
        paragraphs: [
          '‘D&G’ verwijst naar Domestic & General Insurance Europe AG. Domestic & General Insurance Europe AG, Hagenauer Straße 44, 65203 Wiesbaden, Duitsland. HRB nummer 30859. Domestic & General Insurance Europe AG is geautoriseerd en gereguleerd door de Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin) in Duitsland, Nummer in het register voor financiële diensten 5227',
          '‘Cliënt’ verwijst naar Beko Belgium NV',
          '‘We/wij/ons/onze’ verwijst naar Domestic & General Insurance Europe AG.',
          '‘Website’ verwijst naar het sub domein van de inschrijving en van de garantie van de Cliënt, beschikbaar op het adres <0>https://whirlpool.register-appliance.com/RegistroPorMarca?country=BE&language=nl</0> en welke eigendom is van D&G.',
          '‘U/uw’ verwijst naar de gebruiker van deze Website.',
        ],
        links: ['https://whirlpool.register-appliance.com/RegistroPorMarca?country=BE&language=nl'],
      },
      {
        heading: '2. Bedrijfsinformatie',
        paragraphs: [
          'Voorzitter Raad van Commissarissen: David Tyler',
          'Raad van Bestuur: Matthew Crummack (Voorzitter), Thomas Goode, Matthias Kammermann, Sven Wick',
          'BTW Nummer: DE322598066',
          'Belastingnummer: 014 223 10798',
          'Rechtbank van Registratie: Wiesbaden',
        ],
      },
      {
        heading: '3. Gebruik van de website',
        paragraphs: [
          'Het is u toegestaan op deze Website te navigeren en uitsluitend voor persoonlijk gebruik kopieën te maken door deze af te drukken of te downloaden op een harde schijf. Het is niet toegestaan:',
          'Enige inhoud van deze Website op enigerlei wijze te kopiëren, verspreiden, wijzigen of aan te passen, met uitzondering van de hierboven toegestane gevallen;',
          'Enige inhoud van deze Website te kopiëren, verspreiden of anderszins te gebruiken voor commerciële doeleinden;',
          'Enige vermelding van auteursrechten, merk of andere vermelding van intellectuele eigendomsrechten te verwijderen die oorspronkelijk stond vermeld in de gekopieerde of afgedrukte inhoud van de Website.',
          'Alle auteursrechten en alle andere intellectuele eigendomsrechten op de inhoud van deze Website zijn eigendom van D&G.',
          'De Website bevat merken, met name de naam en het logo van de Cliënt, die aan de Cliënt toebehoren, en de naam en het logo van D&G, die aan D&G toebehoren. Geen enkel logo of afbeelding van de Website mag, geheel of gedeeltelijk, worden doorgezonden of gekopieerd zonder de uitdrukkelijke schriftelijke goedkeuring van de eigenaar. In de Vertrouwelijkheids- en veiligheidsverklaring inzake de gegevens op deze Website vindt u meer informatie over de wijze waarop de op deze Website verzamelde persoonsgegevens worden gebruikt.',
        ],
      },
      {
        heading: '4.Inhoud',
        paragraphs: [
          'Alle informatie op deze Website kan zonder voorafgaande kennisgeving worden gewijzigd. Wij verbinden ons er niet toe om deze informatie continu actueel te houden. Wij hebben redelijke maatregelen getroffen om de juistheid en de relevantie van de inhoud van deze website te controleren, maar wij geven geen enkele garantie, expliciet of impliciet, en doen geen verklaringen inzake de inhoud van deze Website, met inbegrip van de handelswaarde, de geschiktheid voor een bepaald gebruik, de status of de wettigheid. Alle informatie wordt ‘als zodanig’ verstrekt en wij kunnen niet aansprakelijk worden gehouden voor verlies of schade voortkomend uit het gebruik van de informatie of adviezen op deze Website; wij kunnen evenmin aansprakelijk worden gehouden voor een fout of weglating in de inhoud van deze Website. Bovendien kunnen wij niet garanderen dat de Website onafgebroken beschikbaar zal zijn, en wij wijzen iedere aansprakelijkheid van de hand voor verlies of schade voortvloeiend uit een dergelijke onbeschikbaarheid.',
        ],
      },
      {
        heading: '5. Links naar externe websites',
        paragraphs: [
          "Deze Website kan links naar externe websites bevatten. Deze koppelingen worden u aangeboden om u te helpen snel en eenvoudig aanvullende informatie te vinden. Wanneer u op een link klikt, verlaat onze Website en de door D&G gecontroleerde informatie en wordt u doorgeleid naar een externe website van derden (‘gelieerde website'). D&G heeft geen invloed op de inhoud van de gelieerde website en is niet aansprakelijk voor de op de gelieerde website verstrekte informatie. Het is voor D&G redelijkerwijs niet mogelijk om constant toezicht te houden op de inhoud van gelieerde websites zonder specifieke aanwijzing voor inbreuk. D&G verwijdert echter een link naar een website als D&G in kennis wordt gesteld over de aanwezigheid van illegale of anderszins strafbare inhoud op de gelieerde website. Wij wijzen iedere aansprakelijkheid van de hand in verband met de inhoud en het gebruik van deze gelieerde websites.",
        ],
      },
      {
        heading: '6. Over ons',
        paragraphs: ['De website wordt in opdracht van de Cliënt beheerd door D&G.'],
      },
      {
        heading: '7. Productregistratiediensten',
        paragraphs: [
          'Domestic & General Insurance Europe AG levert in opdracht van de Cliënt productregistratiediensten.',
        ],
      },
      {
        heading: '8. Belangrijke informatie met betrekking tot de leverancier',
        paragraphs: [
          'Het aanbieden en afsluiten van verzekeringsovereenkomsten deze Website worden verricht door Domestic & General Insurance Europe AG. Alle op deze Website onderschreven verzekeringsovereenkomsten zijn onderhevig aan de algemene polisvoorwaarden die het gebruik daarvan bepalen. U moet de algemene polisvoorwaarden alsook alle andere op deze Website geleverde voorwaarden lezen. Domestic & General Insurance Europe AG is een vennootschap ingeschreven in Duitsland onder nummer 30859 met als statutaire zetel Hagenauer Str. 44, 65203 Wiesbaden. Domestic & General Insurance Europe AG staat onder het toezicht van de Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin), en wordt door deze gereglementeerd onder het registratienummer 5227. U kunt de registers van de BaFin raadplegen op www.bafin.de.',
        ],
      },
      {
        heading: 'Relevante Autoriteit:',
        paragraphs: [
          'Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin)',
          'Graurheindorfer Straße 108',
          '53117 Bonn',
          'Tel: +49 (0)228 410 0',
          'Fax: +49 (0)228 410 81550',
          'In het kader van de derde richtlijn ‘schadeverzekering’ is Domestic & General Insurance Europe AG bevoegd verzekeringsactiviteiten uit te oefenen door een bijkantoor te openen (‘paspoort voor vestiging in de EER’) in de volgende lidstaten van de EER: Frankrijk, Duitsland, Italië en Spanje. De onderneming is ook gemachtigd om verzekeringsdiensten te verrichten op afstand vanuit een van de bovengenoemde hubs ("EER-dienstenpaspoort") in de volgende lidstaten van de EER: Oostenrijk, België, Ierland, Polen, Portugal en Nederland.',
          'Domestic & General Insurance Europe AG oefent in België verzekeringsactiviteiten uit onder de vrijheid van dienstverlening en is daartoe ingeschreven als verzekeringsonderneming bij de Nationale Bank van België onder het nummer 3156 om in België verzekeringsactiviteiten uit te oefenen onder de vrijheid van dienstverlening (www.nbb.be).',
          'Nationale Bank van België (NBB)',
          'de Berlaimontlaan 3',
          '1000 Brussel',
          'Tel: +32 (0)2 221 21 11',
        ],
      },
      {
        heading: '9. Aansprakelijkheid',
        paragraphs: [
          'Wij wijzen iedere aansprakelijkheid van de hand voor storingen, slecht functioneren of onbeschikbaarheid van deze Website, tenzij deze voortkomt uit opzettelijke tekortkoming door ons, uit bedrog door ons of onze grove nalatigheid. Wij wijzen iedere aansprakelijkheid van de hand voor schade of bijzondere verliezen, die onrechtstreeks of incidenteel door u zijn geleden (met inbegrip van winstderving, verlies van zakelijke kansen of van toegevoegde waarde) en die voortvloeien uit het gebruik van deze Website of het slechte functioneren ervan. Niets in deze algemene voorwaarden sluit onze aansprakelijkheid uit of beperkt deze in het geval van overlijden of letsel voortkomend uit onze nalatigheid. Wij kunnen het gebruik van deze Website op ieder moment opschorten, intrekken, beperken of annuleren.',
        ],
      },
      {
        heading: '10. Toepasselijk recht',
        paragraphs: [
          'Het Belgisch recht is van toepassing op deze algemene voorwaarden en uw gebruik van de Website.',
        ],
      },
      {
        heading: '11. Territoriale beperkingen',
        paragraphs: [
          'De Website is op dit moment bestemd voor personen die de Website raadplegen en gebruiken vanuit België..',
        ],
      },
      {
        heading: '12. Virus',
        paragraphs: [
          'Wij nemen redelijke maatregelen om erover te waken dat de Website virusvrij is. Desalniettemin kunnen wij niet aansprakelijk worden gehouden voor enige schade die door een virus of andere technologie wordt veroorzaakt en die voortvloeit uit uw gebruik van deze Website, het downloaden van de inhoud ervan of van de inhoud van een gelieerde website.',
        ],
      },
      {
        heading: '13. Advies en aanbevelingen',
        paragraphs: [
          "Niets op deze Website dient te worden beschouwd als een advies of een aanbeveling. Uw beslissing om al dan niet een verzekeringsovereenkomst op deze Website te kopen, moet u niet uitsluitend baseren op de informatie die op deze pagina's staat vermeld. U moet de polis documenten en/of de algemene voorwaarden van een specifiek product aandachtig lezen voordat u besluit tot aankoop.",
        ],
      },
      {
        heading: '14. Contact met ons opnemen',
        paragraphs: [
          'Als u een zorgpunt wilt uiten betreffende deze Website of als u contact met ons wilt opnemen, kunt u ons als volgt bereiken:',
          'Tel: 0800 10 372',
          'E-Mail: servicebe@domesticandgeneral.com.',
          'Indien u contact op wilt nemen met de Ombudsman van de Verzekeringen, kunt u deze als volgt bereiken:',
          'Ombudsman van de Verzekeringen',
          'de Meeûssquare 35',
          '1000 Brussel',
          'Tel: 02 547 58 71',
          'E-mail: info@ombudsman.as',
          'Bedankt voor uw bezoek aan deze Website. De bovenstaande algemene voorwaarden kunnen door D&G van tijd tot tijd worden gewijzigd en wij bevelen u aan deze algemene voorwaarden bij ieder bezoek aan de Website te controleren.',
        ],
      },
    ],
  },
};
